import { hashKey, MutationCache, matchQuery, QueryClient, type QueryKey } from "@tanstack/react-query";
import { hourMs, minuteMs } from "verdi-common/timeConstants";
import { SessionHandler } from "verdi-web-client";
import { mutationInvalidationTargets } from "./mutation-invalidation-targets";
import { ApiError } from "./orval/api-error";

declare module "@tanstack/react-query" {
    interface Register {
        /** Lets a mutation name the queries it affects. Without this, `meta.invalidates` is untyped. */
        mutationMeta: { invalidates?: QueryKey[] };
    }
}

// The annotation is required, not decoration: the mutation cache below invalidates through `queryClient`,
// so without it the type refers to itself through its own initializer.
export const queryClient: QueryClient = new QueryClient({
    mutationCache: new MutationCache({
        // A successful mutation invalidates every query unless it narrows it with meta.invalidates.
        // Returning the promise is load-bearing: React Query holds the mutation open until it settles, so
        // `await someMutation()` means the refetches have landed. Drop the `return` and every call site
        // that awaits a write and then reads the cache gets the pre-write snapshot instead.
        onSuccess: (_data, _variables, _context, mutation) =>
            queryClient.invalidateQueries({
                // Three states ride on this one line. No `invalidates` -> `?? true` passes every query
                // through, so an untagged mutation stales everything. Roots listed -> only those match. An
                // empty array -> `.some` returns false (not nullish, so `?? true` doesn't fire) and nothing
                // is invalidated.
                predicate: (query) =>
                    mutation.meta?.invalidates?.some((queryKey) => matchQuery({ queryKey }, query)) ?? true,
            }),
    }),
    defaultOptions: {
        queries: {
            // Scope cache per organization to allow for switching organizations
            queryKeyHashFn: (queryKey) => hashKey([SessionHandler.curUser ?? null, ...queryKey]),
            staleTime: 5 * minuteMs,
            gcTime: hourMs,
            refetchOnWindowFocus: false,
            refetchOnReconnect: true,
            networkMode: "offlineFirst",
            // Don't retry 4xx errors since by definition they likely won't succeed on retry
            retry: (failureCount, error) =>
                error instanceof ApiError && error.status < 500 ? false : failureCount < 2,
        },
        mutations: {
            // Match the queries above: attempt the request instead of pausing. React Query's default
            // (`online`) parks a write started while offline and dispatches it whenever the connection
            // returns — long after the UI has reported a failure. Attempting it fails fast instead, so a
            // write the user was told about is the only write that ever reaches the server.
            networkMode: "offlineFirst",
        },
    },
});

// Register mutation invalidations centrally here. A call site can still pass its own meta to override this.
Object.entries(mutationInvalidationTargets).forEach(([mutationKey, invalidates]) => {
    queryClient.setMutationDefaults([mutationKey], { meta: { invalidates } });
});

// The TanStack Query DevTools browser extension discovers the cache through this global.
// Kept out of production: a global handle exposes every cached API response to any script on the page.
declare global {
    interface Window {
        __TANSTACK_QUERY_CLIENT__?: QueryClient;
    }
}

if (import.meta.env.MODE !== "production") {
    window.__TANSTACK_QUERY_CLIENT__ = queryClient;
}
